<template>
  <v-dialog
    v-model="dialog"
    max-width="580"
    persistent
    transition="dialog-transition"
    :overlay-opacity="0.3"
    content-class="dialog-wrapper"
  >
    <v-card class="ultra-modern-dialog" elevation="0">
      <!-- Floating Header with Glass Effect -->
      <div class="floating-header">
        <div class="glass-backdrop"></div>
        <div class="header-content">
          <div class="header-icon-wrapper">
            <div class="icon-ring"></div>
            <v-icon class="header-icon" size="20">mdi-pencil-outline</v-icon>
            <div class="icon-pulse"></div>
          </div>
          <div class="title-section">
            <h2 class="main-title">Edit Message</h2>
            <p class="subtitle">Modify your message content</p>
          </div>
          <div class="header-decoration">
            <div class="deco-dot"></div>
            <div class="deco-dot"></div>
            <div class="deco-dot"></div>
          </div>
        </div>
        <div class="gradient-overlay"></div>
      </div>

      <!-- Original Message Preview Card -->
      <div class="preview-section">
        <div class="section-header">
          <div class="header-line"></div>
          <span class="section-title">
            <v-icon size="16" class="section-icon">mdi-message-text</v-icon>
            Original Message
          </span>
          <div class="header-line"></div>
        </div>

        <div class="message-preview-card">
          <div class="card-glow"></div>
          <div class="message-content-wrapper">
            {{ message?.message || "No message content" }}
          </div>
          <div class="floating-indicator">
            <v-icon size="12" color="primary">mdi-eye-outline</v-icon>
          </div>
        </div>
      </div>

      <!-- Enhanced Input Section -->
      <div class="input-section">
        <div class="input-container">
          <div class="input-header">
            <span class="input-label">New Message Content</span>
            <div class="input-actions">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    v-bind="attrs"
                    v-on="on"
                    @click="clearText"
                    class="action-btn"
                    :disabled="!editedMessageText.trim()"
                  >
                    <v-icon size="16">mdi-close-circle-outline</v-icon>
                  </v-btn>
                </template>
                <span>Clear text</span>
              </v-tooltip>

              <v-menu
                v-model="emojiKeyboard"
                bottom
                offset-y
                :close-on-content-click="false"
                transition="slide-y-transition"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    v-bind="attrs"
                    v-on="on"
                    class="action-btn emoji-btn"
                  >
                    <v-icon size="16">mdi-emoticon-happy-outline</v-icon>
                  </v-btn>
                </template>
                <v-card class="emoji-picker-card">
                  <div class="emoji-header">
                    <span class="emoji-title">Add Emoji</span>
                    <v-btn icon small @click="emojiKeyboard = false">
                      <v-icon size="16">mdi-close</v-icon>
                    </v-btn>
                  </div>
                  <VEmojiPicker @select="selectEmoji" />
                </v-card>
              </v-menu>
            </div>
          </div>

          <div class="advanced-input-wrapper">
            <v-text-field
              v-model="editedMessageText"
              ref="editMessageInput"
              :disabled="loading"
              outlined
              class="premium-input"
              placeholder="Type your message here..."
              @keydown.ctrl.enter="saveChanges"
              @keydown.meta.enter="saveChanges"
            >
              <template v-slot:prepend-inner>
                <div class="input-icon-wrapper">
                  <v-icon class="input-icon" size="18" color="primary"
                    >mdi-message-edit</v-icon
                  >
                </div>
              </template>
            </v-text-field>
          </div>
        </div>
      </div>

      <!-- Premium Action Buttons -->
      <div class="action-section">
        <div class="action-container">
          <div class="button-wrapper">
            <v-btn
              class="premium-btn cancel-btn"
              x-large
              outlined
              @click="cancel"
              :disabled="loading"
              ripple
            >
              <v-icon left size="18">mdi-arrow-left</v-icon>
              Cancel
              <div class="btn-glow"></div>
            </v-btn>

            <v-btn
              class="premium-btn save-btn"
              x-large
              :loading="loading"
              :disabled="!canSave"
              @click="saveChanges"
              elevation="0"
              ripple
            >
              <v-icon left size="18">mdi-check-bold</v-icon>
              Save Changes
              <template v-slot:loader>
                <div class="custom-loader">
                  <div class="loader-ring"></div>
                  <v-icon size="16" color="white">mdi-check</v-icon>
                </div>
              </template>
            </v-btn>
          </div>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import DOMPurify from "dompurify";
export default {
  name: "EditMessage",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    message: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      editedMessageText: "",
      loading: false,
      emojiKeyboard: false,
      isTyping: false,
      typingTimer: null,
      loadingProgress: 0,
      progressInterval: null,
    };
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    canSave() {
      return (
        this.editedMessageText.trim() &&
        this.editedMessageText.trim() !== (this.message?.message || "") &&
        this.editedMessageText.length <= 500
      );
    },
    wordCount() {
      return this.editedMessageText.trim()
        ? this.editedMessageText.trim().split(/\s+/).length
        : 0;
    },
  },
  watch: {
    message: {
      handler(newMessage) {
        if (newMessage && newMessage.message) {
          this.editedMessageText = newMessage.message;
        }
      },
      immediate: true,
    },
    value(isOpen) {
      if (isOpen && this.message) {
        this.editedMessageText = this.message.message || "";
        this.$nextTick(() => {
          if (this.$refs.editMessageInput) {
            this.$refs.editMessageInput.focus();
          }
        });
      }
    },
    editedMessageText() {
      this.handleTyping();
    },
    loading(isLoading) {
      if (isLoading) {
        this.startLoadingProgress();
      } else {
        this.stopLoadingProgress();
      }
    },
  },
  methods: {
    selectEmoji(emoji) {
      this.insertTextAtCursor(emoji.data);
      this.emojiKeyboard = false;
    },

    insertTextAtCursor(text) {
      const input = this.$refs.editMessageInput;
      if (!input || !input.$refs.input) {
        this.editedMessageText += text;
        return;
      }

      const textarea = input.$refs.input;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;

      this.editedMessageText =
        this.editedMessageText.substring(0, start) +
        text +
        this.editedMessageText.substring(end);

      this.$nextTick(() => {
        const newCursorPos = start + text.length;
        textarea.setSelectionRange(newCursorPos, newCursorPos);
        textarea.focus();
      });
    },

    handleTyping() {
      this.isTyping = true;
      clearTimeout(this.typingTimer);
      this.typingTimer = setTimeout(() => {
        this.isTyping = false;
      }, 1000);
    },

    clearText() {
      this.editedMessageText = "";
      this.$nextTick(() => {
        if (this.$refs.editMessageInput) {
          this.$refs.editMessageInput.focus();
        }
      });
    },

    startLoadingProgress() {
      this.loadingProgress = 0;
      this.progressInterval = setInterval(() => {
        if (this.loadingProgress < 90) {
          this.loadingProgress += Math.random() * 10;
        }
      }, 100);
    },

    stopLoadingProgress() {
      clearInterval(this.progressInterval);
      this.loadingProgress = 100;
      setTimeout(() => {
        this.loadingProgress = 0;
      }, 500);
    },
    // Utility sanitizer
    sanitizeInput(value) {
      if (!value) return value;

      // Step 1: Sanitize unsafe HTML but allow text inside <script> to survive
      let clean = DOMPurify.sanitize(value, {
        ALLOWED_TAGS: [],
        ALLOWED_ATTR: [],
      });

      // Step 2: If DOMPurify returns empty string, fallback to extracting inner text
      if (!clean.trim()) {
        // Create a temporary div to extract text content
        const temp = document.createElement("div");
        temp.innerHTML = value;
        clean = temp.textContent || temp.innerText || "";
      }

      return clean.trim();
    },
    saveChanges() {
      if (!this.canSave || this.loading) return;

      this.loading = true;

      // Sanitize the edited message
      const sanitizedText = this.sanitizeInput(this.editedMessageText.trim());

      console.log("EditMessages: Saving changes:", {
        originalMessage: this.message?.message,
        newText: sanitizedText,
      });

      // Emit the sanitized message
      this.$emit("save", {
        message: this.message,
        newText: sanitizedText,
      });

      setTimeout(() => {
        this.loading = false;
      }, 2000);
    },
    cancel() {
      console.log("EditMessages: Cancelling edit");
      this.editedMessageText = this.message?.message || "";
      this.emojiKeyboard = false;
      this.$emit("cancel");
    },
  },

  beforeDestroy() {
    clearTimeout(this.typingTimer);
    clearInterval(this.progressInterval);
  },
};
</script>

<style scoped>
/* Dialog Wrapper */
.dialog-wrapper {
  border-radius: 28px !important;
  backdrop-filter: blur(20px);
  background: rgba(255, 255, 255, 0.95) !important;
}

/* Main Card */
.ultra-modern-dialog {
  border-radius: 28px !important;
  overflow: hidden;
  background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 32px 64px rgba(0, 0, 0, 0.12),
    0 0 0 1px rgba(255, 255, 255, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.1);
  position: relative;
}

/* Floating Header */
.floating-header {
  position: relative;
  padding: 32px 36px 28px;
  /* background: linear-gradient(135deg, #084468 0%, #8b5cf6 50%, #06b6d4 100%); */
  /* background: linear-gradient(135deg,  #084468 0%,#06b6d4 50%, #3b7ca1 100%); */
  background: linear-gradient(135deg, #084468 0%, #3b7ca1 100%);
  overflow: hidden;
}

.glass-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
}

.header-content {
  display: flex;
  align-items: center;
  gap: 20px;
  position: relative;
  z-index: 2;
}

.header-icon-wrapper {
  position: relative;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  animation: rotate 8s linear infinite;
}

.header-icon {
  color: white;
  position: relative;
  z-index: 2;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

.icon-pulse {
  position: absolute;
  width: 100%;
  height: 100%;
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.2) 0%,
    transparent 70%
  );
  border-radius: 50%;
  animation: pulse 2s ease-in-out infinite;
}

.title-section {
  flex: 1;
}

.main-title {
  color: white;
  font-size: 28px;
  font-weight: 800;
  margin: 0 0 4px 0;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  letter-spacing: -0.8px;
  background: linear-gradient(135deg, #ffffff 0%, #e0e7ff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.subtitle {
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  font-weight: 500;
  margin: 0;
  letter-spacing: 0.2px;
}

.header-decoration {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.deco-dot {
  width: 6px;
  height: 6px;
  background: rgba(255, 255, 255, 0.4);
  border-radius: 50%;
  animation: float 3s ease-in-out infinite;
}

.deco-dot:nth-child(2) {
  animation-delay: 0.5s;
}

.deco-dot:nth-child(3) {
  animation-delay: 1s;
}

.gradient-overlay {
  position: absolute;
  top: -50%;
  right: -30%;
  width: 300px;
  height: 300px;
  background: conic-gradient(
    from 0deg,
    rgba(255, 255, 255, 0.1),
    rgba(255, 255, 255, 0.3),
    rgba(255, 255, 255, 0.1)
  );
  border-radius: 50%;
  animation: rotate 20s linear infinite reverse;
  filter: blur(1px);
}

/* Preview Section */
.preview-section {
  padding: 36px;
  background: linear-gradient(145deg, #f8fafc 0%, #f1f5f9 100%);
}

.section-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
}

.header-line {
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, transparent, #cbd5e1, transparent);
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.section-icon {
  color: #084468;
}

.message-preview-card {
  position: relative;
  background: white;
  border-radius: 20px;
  padding: 28px 32px;
  border: 1px solid rgba(99, 102, 241, 0.1);
  box-shadow: 0 8px 32px rgba(99, 102, 241, 0.08), 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.message-preview-card:hover {
  transform: translateY(-4px) scale(1.02);
  box-shadow: 0 20px 48px rgba(99, 102, 241, 0.15),
    0 8px 16px rgba(0, 0, 0, 0.1);
}

.card-glow {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    145deg,
    rgba(99, 102, 241, 0.05) 0%,
    rgba(139, 92, 246, 0.05) 50%,
    rgba(6, 182, 212, 0.05) 100%
  );
  border-radius: 20px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.message-preview-card:hover .card-glow {
  opacity: 1;
}

.message-content-wrapper {
  position: relative;
  z-index: 2;
}

.message-text {
  color: #334155;
  line-height: 1.7;
  font-size: 16px;
  font-weight: 400;
  word-wrap: break-word;
  margin-bottom: 16px;
  min-height: 24px;
}

.message-stats {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.char-count {
  font-size: 12px;
  color: #94a3b8;
  font-weight: 500;
  background: #f1f5f9;
  padding: 4px 12px;
  border-radius: 12px;
}

.floating-indicator {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 32px;
  height: 32px;
  background: rgba(99, 163, 241, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(9, 117, 242, 0.2);
}

/* Input Section */
.input-section {
  padding: 36px;
  background: white;
}

.input-container {
  position: relative;
}

.input-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.input-label {
  font-size: 15px;
  font-weight: 700;
  color: #374151;
  letter-spacing: -0.2px;
}

.input-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  width: 36px !important;
  height: 36px !important;
  background: rgba(99, 165, 241, 0.08) !important;
  border: 1px solid rgba(99, 187, 241, 0.2) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.action-btn:hover {
  background: rgba(99, 179, 241, 0.15) !important;
  transform: scale(1.1);
}

.action-btn:disabled {
  opacity: 0.4;
  transform: none !important;
}

.advanced-input-wrapper {
  position: relative;
}

.premium-input {
  margin-bottom: 2px;
}

.premium-input ::v-deep .v-input__slot {
  border-radius: 18px !important;
  border: 2px solid #e2e8f0 !important;
  background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%) !important;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.02), 0 1px 2px rgba(0, 0, 0, 0.05) !important;
}

/* .premium-input ::v-deep .v-input__slot:hover {
  border-color: #c7d2fe !important;
  background: white !important;
  transform: translateY(-1px);
  box-shadow: 
    inset 0 2px 4px rgba(99, 102, 241, 0.05),
    0 8px 24px rgba(99, 102, 241, 0.1) !important;
} */

/* .premium-input.v-input--is-focused ::v-deep .v-input__slot {
  border-color: #084468 !important;
  background: white !important;
  box-shadow: 
    0 0 0 4px rgba(99, 102, 241, 0.1),
    inset 0 2px 4px rgba(99, 102, 241, 0.05),
    0 12px 32px rgba(99, 102, 241, 0.15) !important;
  transform: translateY(-2px);
} */

.premium-input ::v-deep textarea {
  font-size: 16px !important;
  line-height: 1.6 !important;
  color: #334155 !important;
  padding: 20px 24px !important;
  font-weight: 400 !important;
}

.premium-input ::v-deep textarea::placeholder {
  color: #94a3b8 !important;
  font-weight: 400;
}

.input-icon-wrapper {
  margin-right: 12px;
  padding: 8px;
  background: rgba(99, 182, 241, 0.1);
  border-radius: 12px;
  transition: all 0.3s ease;
}

.premium-input.v-input--is-focused .input-icon-wrapper {
  background: rgba(99, 194, 241, 0.15);
  transform: scale(1.05);
}

.input-enhancements {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
}

.typing-indicator {
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.typing-indicator.active {
  opacity: 1;
}

.typing-dot {
  width: 6px;
  height: 6px;
  background: #084468;
  border-radius: 50%;
  animation: typing-bounce 1.4s ease-in-out infinite both;
}

.typing-dot:nth-child(2) {
  animation-delay: 0.16s;
}

.typing-dot:nth-child(3) {
  animation-delay: 0.32s;
}

.word-count {
  font-size: 12px;
  color: #94a3b8;
  font-weight: 500;
  background: #f8fafc;
  padding: 6px 12px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

/* Emoji Picker */
.emoji-picker-card {
  max-width: 340px;
  max-height: 320px;
  overflow: hidden;
  border-radius: 16px !important;
  box-shadow: 0 20px 48px rgba(0, 0, 0, 0.15) !important;
}

.emoji-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.emoji-title {
  font-weight: 600;
  color: #374151;
  font-size: 14px;
}

/* Action Section */
.action-section {
  padding: 32px 36px 36px;
  background: linear-gradient(145deg, #f8fafc 0%, #ffffff 100%);
  border-top: 1px solid rgba(226, 232, 240, 0.8);
}

.action-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.button-wrapper {
  display: flex;
  gap: 16px;
  justify-content: flex-end;
}

.premium-btn {
  border-radius: 16px !important;
  font-weight: 700 !important;
  text-transform: none !important;
  letter-spacing: -0.2px !important;
  padding: 0 32px !important;
  height: 56px !important;
  position: relative;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.cancel-btn {
  border: 2px solid #e2e8f0 !important;
  color: #64748b !important;
  background: white !important;
  backdrop-filter: blur(10px);
}

.cancel-btn:hover:not(:disabled) {
  border-color: #cbd5e1 !important;
  background: #f8fafc !important;
  transform: translateY(-3px);
  box-shadow: 0 12px 32px rgba(100, 116, 139, 0.2);
}

.save-btn {
  background: linear-gradient(135deg, #084468 0%, #3b7ca1 100%) !important;
  color: white !important;
  border: none !important;
  box-shadow: 0 8px 24px rgba(99, 187, 241, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2) !important;
}

.save-btn:hover:not(:disabled) {
  transform: translateY(-4px) scale(1.05);
  box-shadow: 0 16px 48px rgba(99, 177, 241, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.3) !important;
}

.save-btn:disabled {
  background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%) !important;
  color: #94a3b8 !important;
  box-shadow: none !important;
  transform: none !important;
}

.btn-glow {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  transition: left 0.6s ease;
}

.btn-glow.primary {
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
}

.premium-btn:hover .btn-glow {
  left: 100%;
}

.custom-loader {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.loader-ring {
  width: 24px;
  height: 24px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  position: absolute;
}

.shortcut-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  font-size: 12px;
  color: #94a3b8;
  opacity: 0.8;
  transition: opacity 0.3s ease;
}

.shortcut-hint:hover {
  opacity: 1;
}

.hint-icon {
  opacity: 0.6;
}

/* Loading Overlay */
.premium-loading-overlay {
  border-radius: 28px;
  background: rgba(248, 250, 252, 0.95) !important;
  backdrop-filter: blur(20px);
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  text-align: center;
}

.loading-animation {
  position: relative;
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-rings {
  position: absolute;
  width: 100%;
  height: 100%;
}

.ring {
  position: absolute;
  border: 3px solid transparent;
  border-radius: 50%;
  animation: rotate 3s linear infinite;
}

.ring-1 {
  width: 100%;
  height: 100%;
  border-top: 3px solid #084468;
  animation-duration: 2s;
}

.ring-2 {
  width: 80%;
  height: 80%;
  top: 10%;
  left: 10%;
  border-right: 3px solid #8b5cf6;
  animation-duration: 3s;
  animation-direction: reverse;
}

.ring-3 {
  width: 60%;
  height: 60%;
  top: 20%;
  left: 20%;
  border-bottom: 3px solid #06b6d4;
  animation-duration: 4s;
}

.loading-icon {
  position: relative;
  z-index: 2;
  filter: drop-shadow(0 4px 8px rgba(99, 179, 241, 0.3));
}

.loading-title {
  font-size: 24px;
  font-weight: 800;
  color: #374151;
  margin: 0;
  letter-spacing: -0.5px;
}

.loading-subtitle {
  font-size: 16px;
  color: #64748b;
  margin: 0;
  font-weight: 400;
}

.loading-progress {
  width: 200px;
  height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
  overflow: hidden;
  position: relative;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(135deg, #084468 0%, #06b6d4 50%, #3b7ca1 100%);
  border-radius: 2px;
  transition: width 0.3s ease;
  position: relative;
}

.progress-bar::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.4),
    transparent
  );
  animation: shimmer 1.5s ease-in-out infinite;
}

/* Animations */
@keyframes rotate {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 0.3;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.1);
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-8px);
  }
}

@keyframes typing-bounce {
  0%,
  80%,
  100% {
    transform: scale(0.8) translateY(0);
    opacity: 0.5;
  }
  40% {
    transform: scale(1.2) translateY(-8px);
    opacity: 1;
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translate3d(0, 30px, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translate3d(30px, 0, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .ultra-modern-dialog {
    margin: 20px;
    border-radius: 24px !important;
  }

  .floating-header {
    padding: 28px 28px 24px;
  }

  .main-title {
    font-size: 24px;
  }

  .header-content {
    gap: 16px;
  }

  .header-icon-wrapper {
    width: 48px;
    height: 48px;
  }

  .preview-section,
  .input-section {
    padding: 28px;
  }

  .action-section {
    padding: 28px;
  }

  .button-wrapper {
    flex-direction: column-reverse;
    gap: 12px;
  }

  .premium-btn {
    width: 100%;
    height: 52px !important;
  }

  .message-preview-card {
    padding: 24px;
  }

  .emoji-picker-card {
    max-width: 300px;
    max-height: 280px;
  }

  .loading-animation {
    width: 100px;
    height: 100px;
  }

  .loading-title {
    font-size: 20px;
  }

  .loading-subtitle {
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .ultra-modern-dialog {
    margin: 16px;
    border-radius: 20px !important;
  }

  .floating-header {
    padding: 24px 24px 20px;
  }

  .main-title {
    font-size: 20px;
  }

  .header-decoration {
    display: none;
  }

  .preview-section,
  .input-section,
  .action-section {
    padding: 24px;
  }

  .premium-btn {
    height: 48px !important;
    font-size: 14px !important;
  }

  .shortcut-hint {
    display: none;
  }

  .input-actions {
    gap: 4px;
  }

  .action-btn {
    width: 32px !important;
    height: 32px !important;
  }
}

/* Dark Theme Support */
.theme--dark .ultra-modern-dialog {
  background: linear-gradient(145deg, #1e1e1e 0%, #2d2d30 100%);
  border-color: rgba(255, 255, 255, 0.1);
  box-shadow: 0 32px 64px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba(255, 255, 255, 0.05);
}

.theme--dark .dialog-wrapper {
  background: rgba(30, 30, 30, 0.95) !important;
}

.theme--dark .preview-section {
  background: linear-gradient(145deg, #2d2d30 0%, #3a3a3d 100%);
}

.theme--dark .message-preview-card {
  background: #3a3a3d;
  border-color: rgba(99, 170, 241, 0.2);
  color: #e2e8f0;
}

.theme--dark .message-text {
  color: #e2e8f0;
}

.theme--dark .char-count {
  background: #2d2d30;
  color: #94a3b8;
}

.theme--dark .input-section {
  background: #1e1e1e;
}

.theme--dark .premium-input ::v-deep .v-input__slot {
  background: linear-gradient(145deg, #2d2d30 0%, #3a3a3d 100%) !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
}

.theme--dark .premium-input ::v-deep .v-input__slot:hover {
  border-color: rgba(99, 184, 241, 0.4) !important;
  background: #3a3a3d !important;
}

.theme--dark .premium-input.v-input--is-focused ::v-deep .v-input__slot {
  background: #3a3a3d !important;
  border-color: #084468 !important;
}

.theme--dark .premium-input ::v-deep textarea {
  color: #e2e8f0 !important;
}

.theme--dark .premium-input ::v-deep textarea::placeholder {
  color: #64748b !important;
}

.theme--dark .input-label {
  color: #e2e8f0;
}

.theme--dark .word-count {
  background: #2d2d30;
  color: #94a3b8;
  border-color: rgba(255, 255, 255, 0.1);
}

.theme--dark .action-section {
  background: linear-gradient(145deg, #2d2d30 0%, #1e1e1e 100%);
  border-top-color: rgba(255, 255, 255, 0.1);
}

.theme--dark .cancel-btn {
  background: #2d2d30 !important;
  border-color: rgba(255, 255, 255, 0.2) !important;
  color: #e2e8f0 !important;
}

.theme--dark .cancel-btn:hover:not(:disabled) {
  background: #3a3a3d !important;
  border-color: rgba(255, 255, 255, 0.3) !important;
}

.theme--dark .shortcut-hint {
  color: #64748b;
}

.theme--dark .premium-loading-overlay {
  background: rgba(30, 30, 30, 0.95) !important;
}

.theme--dark .loading-title {
  color: #e2e8f0;
}

.theme--dark .loading-subtitle {
  color: #94a3b8;
}

.theme--dark .loading-progress {
  background: rgba(255, 255, 255, 0.1);
}

.theme--dark .emoji-picker-card {
  background: #2d2d30;
  color: #e2e8f0;
}

.theme--dark .emoji-header {
  background: #3a3a3d;
  border-bottom-color: rgba(255, 255, 255, 0.1);
}

.theme--dark .emoji-title {
  color: #e2e8f0;
}

/* High contrast mode */
@media (prefers-contrast: high) {
  .ultra-modern-dialog {
    border: 2px solid #000;
  }

  .floating-header {
    background: #000;
  }

  .main-title {
    color: #fff;
    text-shadow: none;
  }

  .premium-input ::v-deep .v-input__slot {
    border: 2px solid #000 !important;
  }

  .save-btn {
    background: #000 !important;
    color: #fff !important;
  }
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  .ultra-modern-dialog,
  .premium-btn,
  .message-preview-card,
  .action-btn,
  .premium-input ::v-deep .v-input__slot {
    transition: none !important;
    animation: none !important;
  }

  .header-glow,
  .icon-ring,
  .icon-pulse,
  .deco-dot,
  .gradient-overlay,
  .typing-dot,
  .ring,
  .btn-glow,
  .progress-bar::after {
    animation: none !important;
  }
}

/* Focus visible for accessibility */
.premium-btn:focus-visible,
.action-btn:focus-visible {
  outline: 3px solid #084468;
  outline-offset: 2px;
}

/* Print styles */
@media print {
  .ultra-modern-dialog {
    box-shadow: none;
    border: 1px solid #000;
  }

  .floating-header {
    background: #fff;
    color: #000;
  }

  .main-title {
    color: #000;
  }
}
</style>
